import _ from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {WaveLoader} from '../../../../../../components/Loaders/WaveLoader';
import {useEntityProject} from '../../context';
import {Chat, Message} from '../ChatView/types';
import {TraceCallSchema} from '../wfReactInterface/traceServerClientTypes';
import {
  useThreadMessagesLoader,
  useThreadTurns,
} from '../wfReactInterface/tsDataModelHooks';
import {CallChat} from './CallChat';
import {CallChatLoader} from './CallChatLoader';
import {TurnMetadata} from './TurnMetadata';

// Utility function to create a key from role + content for message deduplication
const createMessageKey = (message: Message): string => {
  const content =
    typeof message.content === 'string'
      ? message.content
      : JSON.stringify(message.content || '');
  return `${message.role || ''}:${content}`;
};

// Check if message has tool_calls in expected format
const hasToolCalls = (message: Message): boolean => {
  return !!(message.tool_calls && Array.isArray(message.tool_calls));
};

// Compare two tool calls by function name and arguments only
const isSameToolCall = (toolCall1: any, toolCall2: any): boolean => {
  // Check for unexpected format
  if (!toolCall1?.function || !toolCall2?.function) {
    return false;
  }

  return (
    toolCall1.function.name === toolCall2.function.name &&
    toolCall1.function.arguments === toolCall2.function.arguments
  );
};

// Compare two messages with tool_calls - requires exact array match
const hasSameToolCalls = (msg1: Message, msg2: Message): boolean => {
  // Both must have tool_calls in expected format
  if (!hasToolCalls(msg1) || !hasToolCalls(msg2)) {
    return false;
  }

  const toolCalls1 = msg1.tool_calls!;
  const toolCalls2 = msg2.tool_calls!;

  // Arrays must have same length (including both empty)
  if (toolCalls1.length !== toolCalls2.length) {
    return false;
  }

  // Compare each tool call in order
  for (let i = 0; i < toolCalls1.length; i++) {
    if (!isSameToolCall(toolCalls1[i], toolCalls2[i])) {
      return false;
    }
  }

  return true;
};

// Phase 1: Cross-chat tool call deduplication using two-cursor approach
const deduplicateToolCalls = (chatN: Chat, chatN1: Chat): Message[] => {
  if (!chatN.request?.messages || !Array.isArray(chatN.request.messages)) {
    return [];
  }
  if (!chatN1.request?.messages || !Array.isArray(chatN1.request.messages)) {
    return chatN.request.messages as Message[];
  }

  const deduplicatedMessages: Message[] = [];
  const chatN1Messages = chatN1.request.messages as Message[];
  const chatNMessages = chatN.request.messages as Message[];
  let lastMatch = 0;

  for (let cursorN = 0; cursorN < chatNMessages.length; cursorN++) {
    const msgN = chatNMessages[cursorN];

    if (hasToolCalls(msgN)) {
      let foundMatch = false;

      for (let i = lastMatch; i < chatN1Messages.length; i++) {
        const msgN1 = chatN1Messages[i];

        if (hasSameToolCalls(msgN, msgN1)) {
          foundMatch = true;
          lastMatch = i + 1; // Update lastMatch to next position
          break;
        }
      }

      if (!foundMatch) {
        // No duplicate found - keep this message
        deduplicatedMessages.push(msgN);
      }
      // If found match, skip this message (it's a duplicate)
    } else {
      // Non-tool-call message - always keep for Phase 2
      deduplicatedMessages.push(msgN);
    }
  }

  return deduplicatedMessages;
};

// Phase 2: Text message deduplication (existing logic)
const deduplicateMessages = (
  messages: Message[],
  seenKeys: Set<string>
): Message[] => {
  return messages.filter(message => {
    const key = createMessageKey(message);
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return true;
  });
};

// Creates a deduplicated version of a Chat with filtered messages
const deduplicateChat = (chat: Chat, seenKeys: Set<string>): Chat => {
  // Clone the chat to avoid mutations
  const deduplicatedChat = {...chat};

  // Check if request has messages array
  if (
    chat.request &&
    chat.request.messages &&
    Array.isArray(chat.request.messages)
  ) {
    const originalMessages = chat.request.messages as Message[];
    const deduplicatedMessages = deduplicateMessages(
      originalMessages,
      seenKeys
    );

    deduplicatedChat.request = {
      ...chat.request,
      messages: deduplicatedMessages,
    };
  }

  return deduplicatedChat;
};

export interface ThreadChatViewProps {
  firstTurnIndex: number;
  turnIds: string[];
  onVisibleTurnChange?: (turnIndex: number) => void;
  groupedChats: Record<string, Array<{chat: Chat; endedAt: string | null}>>;
}

export interface ThreadChatViewRef {
  scrollToTurn: (turnId: string) => void;
}

/**
 * ThreadChatView displays deduplicated chat messages grouped by turn_id.
 *
 * This component renders pre-processed, deduplicated chats that have been
 * grouped by turn_id and sorted by ended_at timestamp. It receives clean
 * chat data and focuses purely on display logic.
 *
 * This component exposes imperative methods through a ref for parent components
 * to interact with the chat view programmatically. It also reports back when
 * the first visible turn changes during scrolling (top-to-bottom detection).
 *
 * @param turnIds - Array of turn IDs in display order
 * @param groupedChats - Pre-processed chats grouped by turn_id with endedAt timestamps
 * @param firstTurnIndex - Starting index for turn numbering
 * @param onVisibleTurnChange - Callback fired when the first visible turn changes during scroll
 *
 * @example
 * ```tsx
 * const chatViewRef = useRef<ThreadChatViewRef>(null);
 *
 * // Scroll to a specific turn
 * chatViewRef.current?.scrollToTurn('turn-123');
 *
 * <ThreadChatView
 *   ref={chatViewRef}
 *   turnIds={turnIds}
 *   groupedChats={sortedGroupedChats}
 *   firstTurnIndex={0}
 *   onVisibleTurnChange={(turnIndex) => console.log('First visible turn index:', turnIndex)}
 * />
 * ```
 */
export const ThreadChatView = forwardRef<
  ThreadChatViewRef,
  ThreadChatViewProps
>(({turnIds, onVisibleTurnChange, firstTurnIndex, groupedChats}, ref) => {
  const turnRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentVisibleTurnIndexRef = useRef<number | null>(null);

  // Debounced callback to report visible turn changes
  const debouncedVisibleTurnChange = useMemo(
    () =>
      _.debounce((turnIndex: number) => {
        if (currentVisibleTurnIndexRef.current !== turnIndex) {
          currentVisibleTurnIndexRef.current = turnIndex;
          onVisibleTurnChange?.(turnIndex);
        }
      }, 1),
    [onVisibleTurnChange]
  );

  // Expose imperative methods to parent components
  useImperativeHandle(
    ref,
    () => ({
      scrollToTurn: (turnId: string) => {
        const turnElement = turnRefs.current.get(turnId);
        if (turnElement) {
          turnElement.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
      },
    }),
    []
  );

  const chatComponents = useMemo(() => {
    if (_.isEmpty(turnIds)) {
      return null;
    }

    return turnIds!.map((turnId, turnIndex: number) => {
      const turnChats = groupedChats[turnId] || [];
      const hasTurnChats = !_.isEmpty(turnChats);

      return (
        <div
          key={`${turnId}-${turnIndex}`}
          data-turn-id={turnId}
          data-turn-index={turnIndex}
          ref={el => {
            if (el) {
              turnRefs.current.set(turnId, el);
            } else {
              turnRefs.current.delete(turnId);
            }
          }}
          className="group px-16 py-8 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-moon-200">
          <TurnMetadata
            turnId={turnId}
            turnNumber={firstTurnIndex + turnIndex + 1}
            hasTurnCalls={hasTurnChats}
          />
          {hasTurnChats &&
            turnChats.map(({chat}, chatIndex: number) => (
              <div className="mt-8" key={chatIndex}>
                <CallChat chat={chat} showTitle={false} />
              </div>
            ))}
        </div>
      );
    });
  }, [turnIds, groupedChats, firstTurnIndex]);

  // Set up scroll-based visibility detection
  // Using scroll handler for reliable, predictable turn detection
  useEffect(() => {
    if (!scrollContainerRef.current || !turnIds?.length || !chatComponents) {
      return;
    }

    let scrollHandler: (() => void) | null = null;
    let container: HTMLElement | null = null;

    // Wait a tick to ensure DOM elements are rendered
    const timeoutId = setTimeout(() => {
      // Scroll-based visibility detection for reliable turn tracking
      scrollHandler = _.throttle(() => {
        if (!scrollContainerRef.current || !turnIds?.length) return;

        container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;

        // Find the first turn that's visible (top edge in viewport)
        let firstVisibleIndex = -1;

        turnRefs.current.forEach((element, turnId) => {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top;
          const elementBottom = rect.bottom;

          // Check if element intersects with container viewport
          const isVisible =
            elementBottom > containerTop &&
            elementTop < containerTop + containerHeight;

          if (isVisible) {
            const turnIndex = turnIds.findIndex(id => id === turnId);
            if (
              turnIndex >= 0 &&
              (firstVisibleIndex === -1 || turnIndex < firstVisibleIndex)
            ) {
              firstVisibleIndex = turnIndex;
            }
          }
        });

        if (firstVisibleIndex >= 0) {
          debouncedVisibleTurnChange(firstVisibleIndex);
        }
      }, 50);

      if (scrollContainerRef.current) {
        scrollContainerRef.current.addEventListener('scroll', scrollHandler, {
          passive: true,
        });

        // Trigger initial detection
        scrollHandler();
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (scrollHandler && container) {
        container!.removeEventListener('scroll', scrollHandler);
        (scrollHandler as any).cancel?.(); // Cancel throttled function
      }
      debouncedVisibleTurnChange.cancel();
    };
  }, [turnIds, chatComponents, debouncedVisibleTurnChange]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex h-full flex-col overflow-y-auto">
      {chatComponents}
    </div>
  );
});

interface ThreadChatViewMessageLoaderProps {
  turnIds: string[];
  onVisibleTurnChange?: (turnIndex: number) => void;
  firstTurnIndex: number;
}

/**
 * ThreadChatViewMessageLoader handles the fan-out loading pattern and chat deduplication.
 *
 * This component:
 * 1. Loads raw message data via useThreadMessagesLoader
 * 2. Triggers useCallAsChat for all calls concurrently (fan-out pattern)
 * 3. Deduplicates chat messages across all turns as they load
 * 4. Groups and sorts chats by turn_id and ended_at timestamp
 * 5. Renders ThreadChatView only when all chats are loaded and processed
 *
 * The key prop ensures this component remounts when turnIds change,
 * providing fresh state for each new set of thread data.
 */
export const ThreadChatViewMessageLoader = forwardRef<
  ThreadChatViewRef,
  ThreadChatViewMessageLoaderProps
>(({turnIds, onVisibleTurnChange, firstTurnIndex}, ref) => {
  const {projectId} = useEntityProject();
  const {messagesState, loaded} = useThreadMessagesLoader(projectId, turnIds);

  // Check for empty raw messages
  const isEmpty = !messagesState.value || messagesState.value.length === 0;

  // Extract all calls for concurrent chat loading (fan-out pattern)
  const allCalls = useMemo(() => {
    return messagesState.value || [];
  }, [messagesState.value]);

  // Store raw chat data in ref to avoid re-renders (deduplication happens later)
  const groupedChatsRef = useRef<
    Record<string, Array<{chat: Chat; endedAt: string | null}>>
  >({});
  const [, setLoadedChatCount] = useState(0); // Track loading progress
  const [chatsReady, setChatsReady] = useState(false);

  // Handle incoming chat data from CallChatLoader components
  const handleChatLoaded = useCallback(
    (call: TraceCallSchema, chat: Chat) => {
      const turnId = call.turn_id!;
      const endedAt = call.ended_at!;

      // Store raw chat data without deduplication (deduplication happens in sortedGroupedChats)
      if (!groupedChatsRef.current[turnId]) {
        groupedChatsRef.current[turnId] = [];
      }
      groupedChatsRef.current[turnId].push({chat, endedAt});

      // Track loading progress and mark ready when all chats are processed
      setLoadedChatCount(prev => {
        const newCount = prev + 1;
        if (newCount >= allCalls.length && allCalls.length > 0) {
          setChatsReady(true);
        }
        return newCount;
      });
    },
    [allCalls.length]
  );

  // Sort chats chronologically and apply deduplication once all are loaded
  const sortedGroupedChats = useMemo(() => {
    if (!chatsReady) {
      return undefined;
    }

    // Collect all chats with their metadata for global chronological sorting
    const allChatsWithMetadata: Array<{
      chat: Chat;
      endedAt: string | null;
      turnId: string;
    }> = [];

    Object.keys(groupedChatsRef.current).forEach(turnId => {
      groupedChatsRef.current[turnId].forEach(({chat, endedAt}) => {
        allChatsWithMetadata.push({chat, endedAt, turnId});
      });
    });

    // Sort all chats chronologically across all turns
    allChatsWithMetadata.sort((a, b) => {
      if (_.isEmpty(a.endedAt) && _.isEmpty(b.endedAt)) return 0;
      if (_.isEmpty(a.endedAt)) return 1; // empty values last
      if (_.isEmpty(b.endedAt)) return -1;
      return a.endedAt!.localeCompare(b.endedAt!);
    });

    // Phase 1: Tool call deduplication - O(n) pass (backward to preserve earlier messages)
    const toolCallDeduplicatedChats = [...allChatsWithMetadata]; // Clone array

    // Loop backward from last chat to second chat (skip first chat)
    for (let i = allChatsWithMetadata.length - 1; i > 0; i--) {
      const {chat, endedAt, turnId} = toolCallDeduplicatedChats[i];
      const previousChat = toolCallDeduplicatedChats[i - 1].chat;

      const toolCallDeduplicatedMessages = deduplicateToolCalls(
        chat,
        previousChat
      );

      const deduplicatedChat = {
        ...chat,
        request: chat.request
          ? {
              ...chat.request,
              messages: toolCallDeduplicatedMessages,
            }
          : null,
      };

      toolCallDeduplicatedChats[i] = {chat: deduplicatedChat, endedAt, turnId};
    }

    // Phase 2: Text message deduplication - O(n) pass
    const seenMessageKeys = new Set<string>();
    const deduplicatedChatsWithMetadata = toolCallDeduplicatedChats.map(
      ({chat, endedAt, turnId}) => ({
        chat: deduplicateChat(chat, seenMessageKeys),
        endedAt,
        turnId,
      })
    );

    // Group deduplicated chats back by turn_id
    const sorted: Record<
      string,
      Array<{chat: Chat; endedAt: string | null}>
    > = {};

    deduplicatedChatsWithMetadata.forEach(({chat, endedAt, turnId}) => {
      if (!sorted[turnId]) {
        sorted[turnId] = [];
      }
      sorted[turnId].push({chat, endedAt});
    });

    // Sort within each turn by ended_at
    Object.keys(sorted).forEach(turnId => {
      sorted[turnId].sort((a, b) => {
        if (_.isEmpty(a.endedAt) && _.isEmpty(b.endedAt)) return 0;
        if (_.isEmpty(a.endedAt)) return 1; // empty values last
        if (_.isEmpty(b.endedAt)) return -1;
        return a.endedAt!.localeCompare(b.endedAt!);
      });
    });

    return sorted;
  }, [chatsReady]);

  // Create CallChatLoader components for concurrent chat processing (fan-out)
  const callChatLoaders = useMemo(() => {
    return allCalls.map((call, index) => (
      <CallChatLoader
        key={`${call.id}-${index}`}
        call={call}
        onChatLoaded={handleChatLoaded}
      />
    ));
  }, [allCalls, handleChatLoaded]);

  if (loaded && isEmpty) {
    return (
      <div className="flex h-full w-full items-center justify-center text-moon-500">
        No messages found in this thread.
      </div>
    );
  }

  return (
    <>
      {/* Fan-out pattern: trigger useCallAsChat concurrently for all calls */}
      {callChatLoaders}

      {/* Render chat view only when all chats are loaded and deduplicated */}
      {chatsReady && sortedGroupedChats && (
        <ThreadChatView
          ref={ref}
          turnIds={turnIds}
          onVisibleTurnChange={onVisibleTurnChange}
          firstTurnIndex={firstTurnIndex}
          groupedChats={sortedGroupedChats}
        />
      )}
    </>
  );
});

interface ThreadChatViewStagingProps {
  turnsState: ReturnType<typeof useThreadTurns>['turnsState'];
  onVisibleTurnChange?: (turnIndex: number) => void;
  firstTurnIndex: number;
}

/**
 * ThreadChatViewStaging converts turn state to turnIds and delegates to ThreadChatViewMessageLoader.
 *
 * This component serves as an adapter between the turns-based data structure
 * and the message loader. It extracts turnIds from the turns state and uses
 * a key prop to ensure the loader resets when turn data changes.
 */
export const ThreadChatViewStaging = forwardRef<
  ThreadChatViewRef,
  ThreadChatViewStagingProps
>(({turnsState, onVisibleTurnChange, firstTurnIndex}, ref) => {
  const turnIds = useMemo(() => {
    if (turnsState.value) {
      return turnsState.value.turnCalls.map(turn => turn.id);
    }
    return [];
  }, [turnsState.value]);

  if (turnsState.loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <WaveLoader size="small" />
      </div>
    );
  }

  if (turnsState.error) {
    return (
      <div className="flex h-full w-full items-center justify-center text-moon-500">
        Error loading messages: {turnsState.error.message}
      </div>
    );
  }

  return (
    <ThreadChatViewMessageLoader
      key={turnIds.join(',')}
      ref={ref}
      turnIds={turnIds}
      onVisibleTurnChange={onVisibleTurnChange}
      firstTurnIndex={firstTurnIndex}
    />
  );
});
